import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { Button, Link, useConfirmationDialog } from '@factorialco/deprecated-design-system'
import { ApplicationsDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  AtsGetCvBulkUploadsDocument,
  AtsUploadCvFileFragment,
  AtsUploadCvsWidgetFragment,
} from 'generated/resources/graphql'
import { countBy } from 'lodash'

import Callout from 'modules/core/components/Callout'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { UploadCvsSidepanel } from './Sidepanel'
import { useUpdateCvBulkUploadStatus } from './useUpdateCvBulkUploadStatus'

interface Props {
  cvBulkUpload: AtsUploadCvsWidgetFragment
}

export const useCvBulkUpload = (atsJobPostingId = null) => {
  const { data, loading, stopPolling, startPolling, client } = useQuery(
    AtsGetCvBulkUploadsDocument,
    {
      variables: { atsJobPostingId },
      pollInterval: 5000,
      onCompleted: async (newData) => {
        const cvBulkUploadData = newData?.ats.cvBulkUploadsConnection?.edges[0]?.node
        const inProgress =
          cvBulkUploadData?.status === 'processing' || cvBulkUploadData?.status === 'cancelling'

        if (cvBulkUploadData && inProgress) {
          startPolling(5000)
          return
        }

        if (atsJobPostingId && !inProgress) {
          client.refetchQueries({ include: [ApplicationsDataTableDocument] })
        }

        stopPolling()
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
    }
  )

  const cvBulkUpload = data?.ats.cvBulkUploadsConnection.edges[0]?.node
  if (cvBulkUpload?.status === 'archived') {
    return { cvBulkUpload: null, loading }
  }

  return { cvBulkUpload, loading }
}

const countFilesByStatus = (files: AtsUploadCvFileFragment[]) => {
  const counts = countBy(files, (file) => {
    if (file.candidate) return 'completed'
    if (file.processingError === 'cv_bulk_upload_cancelled') return 'cancelled'
    if (file.processingError) return 'errored'
    return 'processing'
  })

  return {
    completed: counts.completed || 0,
    errored: counts.errored || 0,
    processing: counts.processing || 0,
    cancelled: counts.cancelled || 0,
  }
}

export const UploadCvsWidget = ({ cvBulkUpload }: Props) => {
  const { openDialog } = useConfirmationDialog()
  const [showDetailsSidepanel, setDetailsSidepanel] = useState(false)

  const { updateBulkUploadStatus, updatingStatus } = useUpdateCvBulkUploadStatus(cvBulkUpload.id)
  const { company } = useCurrentSession()
  const { jobId } = useParams()

  const totalFiles = cvBulkUpload.files.length
  const importInProgress = cvBulkUpload.status === 'processing'
  const importCancelling = cvBulkUpload.status === 'cancelling'
  const importCancelled = cvBulkUpload.status === 'cancelled'
  const importCompleted = cvBulkUpload.status === 'completed'
  const filesByStatus = countFilesByStatus(cvBulkUpload.files)
  const uploadedFiles = totalFiles - filesByStatus.processing

  const statusLabel = (() => {
    if (importInProgress) {
      return {
        type: 'info',
        title: i18n.t('ats.candidates.upload_resumes.widget.in_progress.title_with_progress', {
          current: uploadedFiles,
          total: totalFiles,
        }),
        description: i18n.t('ats.candidates.upload_resumes.widget.in_progress.description'),
      } as const
    }

    if (importCancelling) {
      return {
        type: 'warning',
        description: '',
        title: i18n.t('ats.candidates.upload_resumes.widget.cancelling.title'),
      } as const
    }

    if (importCancelled) {
      return {
        type: 'warning',
        description: i18n.t(
          'ats.candidates.upload_resumes.widget.cancelled.description_with_progress',
          {
            current: totalFiles - (filesByStatus.cancelled + filesByStatus.errored),
            total: totalFiles,
          }
        ),
        title: i18n.t('ats.candidates.upload_resumes.widget.cancelled.title'),
      } as const
    }

    if (filesByStatus.errored > 0) {
      return {
        type: 'critical',
        title: i18n.tp('ats.candidates.upload_resumes.widget.failed.title', {
          count: filesByStatus.errored,
        }),
        description: i18n.t('ats.candidates.upload_resumes.widget.failed.description'),
      } as const
    }

    return {
      type: 'positive',
      title: i18n.t('ats.candidates.upload_resumes.widget.completed.title'),
      description: i18n.tp('ats.candidates.upload_resumes.widget.completed.description', {
        count: filesByStatus.completed,
      }),
    } as const
  })()

  const onClose = async () => {
    if (updatingStatus) return

    const result = await updateBulkUploadStatus('archived')
    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('ats.candidates.upload_resumes.close_submit_success'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('ats.candidates.upload_resumes.close_submit_error'),
      })
    }
  }

  const onCancel = async () => {
    if (updatingStatus || !importInProgress) return

    const result = await updateBulkUploadStatus('cancelling')
    const handled = handleResult(result, () => undefined)
    const cancelledEvent = !!jobId
      ? trackingEvents.ats.cvBulkUploadApplicantsCancelled
      : trackingEvents.ats.cvBulkUploadCancelled

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('ats.candidates.upload_resumes.cancel_modal.cancel_submit_success'),
      })

      track(cancelledEvent, {
        company_id: company.id,
      })
    }
  }

  return (
    <>
      <Callout
        type={statusLabel.type}
        title={statusLabel.title}
        description={statusLabel.description}
        action={
          <>
            <Link.Touchable
              title={i18n.t('ats.candidates.upload_resumes.details_button')}
              type="solid"
              onClick={() => setDetailsSidepanel(true)}
            >
              {i18n.t('ats.candidates.upload_resumes.details_button')}
            </Link.Touchable>

            {(importInProgress || importCancelling) && (
              <Button
                loading={importCancelling}
                type="secondary"
                onClick={() =>
                  openDialog({
                    illustration: 'delete-cross',
                    title: i18n.t('ats.candidates.upload_resumes.cancel_modal.title'),
                    content: i18n.t('ats.candidates.upload_resumes.cancel_modal.description'),
                    confirmLabel: i18n.t(
                      'ats.candidates.upload_resumes.cancel_modal.confirm_button'
                    ),
                    cancelLabel: i18n.t('ats.candidates.upload_resumes.cancel_modal.cancel_button'),
                    onConfirm: onCancel,
                  })
                }
                iconName="cross-circle"
              >
                {i18n.t('common.cancel')}
              </Button>
            )}
          </>
        }
        onRemove={
          importCompleted || importCancelled
            ? () => {
                openDialog({
                  illustration: 'delete-cross',
                  title: i18n.t('ats.candidates.upload_resumes.close_confirmation.title'),
                  content: i18n.t('ats.candidates.upload_resumes.close_confirmation.description'),
                  confirmLabel: i18n.t('ats.candidates.upload_resumes.close_confirmation.confirm'),
                  cancelLabel: i18n.t('ats.candidates.upload_resumes.close_confirmation.cancel'),
                  onConfirm: onClose,
                })
              }
            : undefined
        }
      />

      {showDetailsSidepanel && (
        <UploadCvsSidepanel
          cvBulkUpload={cvBulkUpload}
          onClose={() => setDetailsSidepanel(false)}
        />
      )}
    </>
  )
}
